import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { ExternalLink } from 'lucide-react'
import React, { useEffect, useState } from 'react'

import { adminRecordInspector } from 'rails_routes'
import { Badge } from 'slab'
import {
  useGetAdminMappedPlans,
} from 'utils/web_api'
import { type AdminMappedPlan } from 'utils/web_api.schemas'

import CarrierNameFilter from './CarrierNameFilter'
import { DeactivatedFilter } from './DeactivatedFilter'
import { DifferentCarriersFilter } from './DifferentCarriersFilter'
import { EmployerRenewalStatusFilter } from './EmployerRenewalStatusFilter'
import { PlanMappingActions } from './PlanMappingActions'
import SourceFilter from './SourceFilter'
import StateFilter from './StateFilter'
import { usePlanTableFilters } from './usePlanTableFilters'
import { type FilterConfigurations } from '../../reusable/filters/EnterpriseFiltersView'
import TanstackTable, { type PaginationMetadata } from '../../reusable/table/TanstackTable'
import SyncFiltersWithUrl from '../SyncFiltersWithUrl'

const PAGE_SIZE = 20

const columnHelper = createColumnHelper<AdminMappedPlan>()

// Helper function to determine descendant plan source and data
const getDescendantPlanData = (plan: AdminMappedPlan) => {
  // Prioritize Ideon first, then fallback to Thatch
  const source = plan.ideon_descendant_plan_id ? 'Ideon' : 'Thatch'

  return {
    source,
    id: plan.ideon_descendant_plan_id || plan.thatch_mapped_plan_id,
    hiosId: plan.ideon_descendant_plan_hios_id || plan.thatch_mapped_plan_hios_id,
    carrierName: plan.ideon_descendant_plan_carrier_name || plan.thatch_mapped_plan_carrier_name,
    planName: plan.ideon_descendant_plan_display_name || plan.thatch_mapped_plan_display_name
  }
}

// Helper function to get badge type for source
const getBadgeType = (source: string) => {
  switch (source) {
    case 'Ideon':
      return 'info'         // Blue for Ideon
    case 'Thatch':
      return 'thatch'       // Purple (Thatch brand color)
    default:
      return 'neutral'
  }
}

const createColumns = (refetch: () => void) => [
  columnHelper.accessor('state', {
    header: 'State',
  }),
  columnHelper.accessor('ancestor_plan_id', {
    header: 'Medical Plan ID',
    cell: ({ getValue }) => (
      <div className='flex items-center gap-2'>
        <span className='font-medium text-gray-900'>
          {getValue()?.slice(-6)}
        </span>
        <button
          onClick={() => window.open(adminRecordInspector.show({ id: getValue() }))}
          className='text-gray-400 hover:text-blue-600 transition-colors'
          title='Inspect medical plan'
        >
          <ExternalLink className='w-4 h-4' />
        </button>
      </div>
    ),
  }),
  columnHelper.accessor('ancestor_plan_carrier_name', {
    header: 'Ancestor Carrier Name',
  }),
  columnHelper.accessor('ancestor_plan_display_name', {
    header: 'Ancestor Plan Name',
  }),
  columnHelper.accessor('ancestor_plan_hios_id', {
    header: 'Ancestor HIOS ID',
  }),
  columnHelper.display({
    header: 'Source',
    cell: ({ row }) => {
      const { source } = getDescendantPlanData(row.original)

      return (
        <div className='flex items-center gap-1'>
          <Badge type={getBadgeType(source)}>{source}</Badge>
        </div>
      )
    },
  }),
  columnHelper.display({
    header: 'Descendant Plan ID',
    cell: ({ row }) => {
      const { id } = getDescendantPlanData(row.original)

      return id
        ? (
          <div className='flex items-center gap-2'>
            <span className='font-medium text-gray-900'>
              {id.slice(-6)}
            </span>
            <button
              onClick={() => window.open(adminRecordInspector.show({ id }))}
              className='text-gray-400 hover:text-blue-600 transition-colors'
              title='Inspect descendant plan'
            >
              <ExternalLink className='w-4 h-4' />
            </button>
          </div>
          )
        : <span className='text-gray-400'>-</span>
    },
  }),
  columnHelper.display({
    header: 'Descendant HIOS ID',
    cell: ({ row }) => {
      const { hiosId } = getDescendantPlanData(row.original)
      return (hiosId)
    },
  }),
  columnHelper.display({
    header: 'Descendant Carrier Name',
    cell: ({ row }) => {
      const { carrierName } = getDescendantPlanData(row.original)
      return (carrierName)
    },
  }),
  columnHelper.display({
    header: 'Descendant Plan Name',
    cell: ({ row }) => {
      const { planName } = getDescendantPlanData(row.original)
      return (planName)
    },
  }),
  columnHelper.display({
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => {
      const { source } = getDescendantPlanData(row.original)
      const {
        plan_mapping_id: planMappingId,
        is_active: isActive,
        ancestor_plan_display_name: ancestorPlanDisplayName,
      } = row.original

      // Only show actions for Thatch mappings
      if (source !== 'Thatch' || !planMappingId || isActive === undefined) {
        return <span className='text-gray-400'>-</span>
      }

      return (
        <PlanMappingActions
          planMappingId={planMappingId}
          planName={ancestorPlanDisplayName}
          isActive={isActive}
          onSuccess={refetch}
        />
      )
    },
  }),
]

interface MappedPlansFilters extends Record<string, string | string[]> {
  states: string[]
  carrier_names: string[]
  source: string
  show_deactivated: string // Store as string for URL sync compatibility
  renewed_employers_only: string // Store as string for URL sync compatibility
  different_carriers_only: string // Store as string for URL sync compatibility
}

type MappedPlansFilterGroup = 'Basic Filters' | 'Status Filters'

const filterConfigurations: FilterConfigurations<MappedPlansFilters, MappedPlansFilterGroup> = {
  states: {
    key: 'states',
    label: 'States',
    group: 'Basic Filters',
    isMultiValue: true,
  },
  carrier_names: {
    key: 'carrier_names',
    label: 'Carrier Names',
    group: 'Basic Filters',
    isMultiValue: true,
  },
  source: {
    key: 'source',
    label: 'Source',
    group: 'Basic Filters',
    isMultiValue: false,
  },
  show_deactivated: {
    key: 'show_deactivated',
    label: 'Show Deactivated',
    group: 'Status Filters',
    isMultiValue: false,
  },
  renewed_employers_only: {
    key: 'renewed_employers_only',
    label: 'Renewed Employers Only',
    group: 'Status Filters',
    isMultiValue: false,
  },
  different_carriers_only: {
    key: 'different_carriers_only',
    label: 'Different Carriers Only',
    group: 'Status Filters',
    isMultiValue: false,
  },
}

interface MappedPlansTableProps {
  pageParam?: number
}

const MappedPlansTable: React.FC<MappedPlansTableProps> = ({
  pageParam,
}) => {
  const [paginationMetadata, setPaginationMetadata] = useState<
    PaginationMetadata | undefined
  >()

  const [pagination, setPagination] = useState({
    pageIndex: pageParam || 1,
    pageSize: PAGE_SIZE,
  })

  const {
    filters,
    setFilters,
    handleMultiSelectToggle,
    handleSingleSelectToggle,
    clearFilters: clearFiltersBase,
    getActiveFilterCount,
  } = usePlanTableFilters<MappedPlansFilters>({
    initialFilters: {
      states: [],
      carrier_names: [],
      source: '',
      show_deactivated: '',
      renewed_employers_only: '',
      different_carriers_only: '',
    },
    onFiltersChange: () => setPagination(prev => ({ ...prev, pageIndex: 1 })),
  })

  // Build query parameters with filters
  const queryParams = {
    'page[number]': pagination.pageIndex,
    'page[size]': pagination.pageSize,
    ...(filters.states.length > 0 && {
      'filters[states][]': filters.states,
    }),
    ...(filters.carrier_names.length > 0 && {
      'filters[carrier_names][]': filters.carrier_names,
    }),
    ...(filters.source && {
      'filters[source]': filters.source,
    }),
    ...(filters.show_deactivated === 'true' && {
      'filters[show_deactivated]': true,
    }),
    ...(filters.renewed_employers_only === 'true' && {
      'filters[renewed_employers_only]': true,
    }),
    ...(filters.different_carriers_only === 'true' && {
      'filters[different_carriers_only]': true,
    }),
  }

  const { data, isLoading, error, refetch } = useGetAdminMappedPlans(queryParams)

  useEffect(() => {
    if (data) {
      setPaginationMetadata(data.pagination)
    }
  }, [data?.pagination])

  const clearFilters = () => {
    clearFiltersBase()
    setPagination(prev => ({ ...prev, pageIndex: 1 }))
  }

  const columns = createColumns(refetch)

  const table = useReactTable({
    data: data?.data || [],
    columns,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    state: {
      pagination,
    },
  })

  return (
    <div className='space-y-4'>
      <SyncFiltersWithUrl
        filters={filters}
        setFilters={setFilters}
        pagination={pagination}
        filterConfigurations={filterConfigurations}
      />

      {/* Filters Section */}
      <div className='bg-white rounded-lg border shadow-sm p-4'>
        <div className='flex items-center justify-between mb-3'>
          <h3 className='text-sm font-medium text-gray-900'>Filters</h3>
          {getActiveFilterCount() > 0 && (
            <button
              onClick={clearFilters}
              className='text-sm text-blue-600 hover:text-blue-800 font-medium'
            >
              Clear all ({getActiveFilterCount()})
            </button>
          )}
        </div>

        {/* Dropdown/Multiselect Filters */}
        <div className='flex flex-wrap gap-4 mb-4'>
          <StateFilter
            data-test-id='state-filter'
            selectedValues={filters.states}
            onSelectValue={(value) => handleMultiSelectToggle('states', value)}
          />

          <CarrierNameFilter
            selectedValues={filters.carrier_names}
            onSelectValue={(value) => handleMultiSelectToggle('carrier_names', value)}
          />

          <SourceFilter
            selectedValues={filters.source ? [filters.source] : []}
            onSelectValue={(value) => handleSingleSelectToggle('source', value)}
          />
        </div>

        {/* Toggle Filters */}
        <div className='flex flex-col gap-2'>
          <DeactivatedFilter
            showDeactivated={filters.show_deactivated === 'true'}
            onToggle={() => handleSingleSelectToggle('show_deactivated', 'true')}
          />

          <EmployerRenewalStatusFilter
            renewedEmployersOnly={filters.renewed_employers_only === 'true'}
            onToggle={() => handleSingleSelectToggle('renewed_employers_only', 'true')}
          />

          <DifferentCarriersFilter
            differentCarriersOnly={filters.different_carriers_only === 'true'}
            onToggle={() => handleSingleSelectToggle('different_carriers_only', 'true')}
          />
        </div>
      </div>

      <TanstackTable
        table={table}
        isLoading={isLoading}
        isError={Boolean(error)}
        noDataMessage='No mapped plans found. All plans require descendant mappings.'
        paginationLabelSingular='plan'
        paginationMetadata={paginationMetadata}
        onNextPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.next_page || 1,
          })}
        onPreviousPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.prev_page || 1,
          })}
      />
    </div>
  )
}

export default MappedPlansTable
